import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useResponsive } from './ResponsiveContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import {
  GetTicketsCountByRoofTopResponseDTO,
  GetTicketsRequestParamsDTO,
  GetTicketsResponseDTO,
} from 'declarations/dto/tickets';
import { SortingOrderE } from 'declarations/enums/common';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import {
  AgeFilterValuesE,
  ParameterSortingValuesE,
  ScheduleFilterValuesE,
  StatusFilterValuesE,
  TicketsRefinementKeysE,
  TicketsViewModeE,
} from 'declarations/enums/tickets';
import { RoofTopI } from 'declarations/interfaces/roofTop';
import { TicketCountByRoofTopI } from 'declarations/interfaces/tickets';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import { useFetchTickets, useFetchTicketsCountByRoofTop } from 'hooks/api/useTickets';

export type TicketsContextT = {
  ticketsPages: GetTicketsResponseDTO[] | undefined | null;
  ticketsCount: number;
  ticketsCountByRoofTop: GetTicketsCountByRoofTopResponseDTO | null | undefined;
  mappedRooftopsList: TicketCountByRoofTopI[] | null | undefined;
  isTicketsFetchingError: boolean;
  fetchTicketsErrorMessage: string | undefined;
  fetchTicketsValidationErrorMessage: string | undefined;
  isTicketsFetchingCountByRoofTopError: boolean;
  isSuccessfullyFetchedTickets: boolean;
  isLoadingTickets: boolean;
  isLoadingTicketsCountByRoofTop: boolean;
  setSidebarIsOpen: (isSidebarOpened: boolean) => void;
  isSidebarOpened: boolean;
  setTicketsViewMode: (ticketsViewMode: TicketsViewModeE) => void;
  ticketsViewMode: TicketsViewModeE;
  setTicketId: (id: string | null) => void;
  selectedTicketId: string | null;
  isNewTicketPopupOpened: boolean;
  setNewTicketPopupVisibility: (isOpened: boolean) => void;
  isROListPopupOpened: boolean;
  setROListPopupVisibility: (isOpened: boolean) => void;
  getSearchParam: (key: TicketsRefinementKeysE) => string | null;
  updateSearchParam: (key: TicketsRefinementKeysE, value: string) => void;
  clearSearchParam: (keys: TicketsRefinementKeysE | TicketsRefinementKeysE[]) => void;
  isFetchingNextTicketsPage: boolean;
  isFetchingTickets: boolean;
  hasNextTicketsPage: boolean | undefined;
  fetchNextTicketsPage: () => void;
  fetchTicketsParams: GetTicketsRequestParamsDTO;
  activeScheduleCards: string[];
  setActiveScheduleCards: (cards: string[]) => void;
  activeScheduleFilters: ScheduleFilterValuesE[];
  setActiveScheduleFilters: (filters: ScheduleFilterValuesE[]) => void;
  getAllSearchParam: (key: TicketsRefinementKeysE) => number[];
  updateSearchParamsArray: (
    key: TicketsRefinementKeysE,
    value: (number | string | ScheduleFilterValuesE)[] | null
  ) => void;
  selectedRoofTop: string;
  scheduleCategoryId: number;
  setScheduleCategoryId: (id: number) => void;
  dealerSelectedRoofTopCount: number;
  setDealerSelectedRoofTopCount: (count: number) => void;
  dealerSelectedRooftopIds: string | null;
  setDealerSelectedRooftopIds: (id: string | null) => void;
  initialSelectedRooftopIds: string[];
  setInitialSelectedRooftopIds: (ids: string[]) => void;
  roofTopsList: RoofTopI[] | null;
  shouldLoadMoreRooftops: boolean;
  hasNextRooftopsPage: boolean;
  fetchNextRooftopsPage: () => void;
  selectedTicketsIds: string[];
  setSelectedTicketsIds: (id: string[]) => void;
  activeTab: ActiveTabTypeE;
  setActiveTab: (tab: ActiveTabTypeE) => void;
  isScheduleTicketList: boolean;
  setIsScheduleTicketList: (value: boolean) => void;
  ticketsTotalCount: number | undefined;
};

type TicketsProviderPropsT = { children: ReactNode };

const contextDefaultValue = {
  ticketsPages: null,
  ticketsCount: 0,
  ticketsCountByRoofTop: null,
  mappedRooftopsList: null,
  isTicketsFetchingError: false,
  fetchTicketsErrorMessage: undefined,
  fetchTicketsValidationErrorMessage: undefined,
  isTicketsFetchingCountByRoofTopError: false,
  isSuccessfullyFetchedTickets: false,
  isLoadingTickets: false,
  isLoadingTicketsCountByRoofTop: false,
  setSidebarIsOpen: () => undefined,
  isSidebarOpened: true,
  setTicketsViewMode: () => undefined,
  ticketsViewMode: TicketsViewModeE.List,
  setTicketId: () => undefined,
  selectedTicketId: '',
  isNewTicketPopupOpened: false,
  setNewTicketPopupVisibility: () => undefined,
  isROListPopupOpened: false,
  setROListPopupVisibility: () => undefined,
  getSearchParam: () => null,
  updateSearchParam: () => undefined,
  clearSearchParam: () => undefined,
  isFetchingNextTicketsPage: false,
  isFetchingTickets: false,
  hasNextTicketsPage: false,
  fetchNextTicketsPage: () => null,
  fetchTicketsParams: {},
  activeScheduleCards: [],
  setActiveScheduleCards: () => undefined,
  activeScheduleFilters: [],
  setActiveScheduleFilters: () => undefined,
  getAllSearchParam: () => [],
  updateSearchParamsArray: () => undefined,
  selectedRoofTop: '',
  scheduleCategoryId: 0,
  setScheduleCategoryId: () => undefined,
  dealerSelectedRoofTopCount: 0,
  setDealerSelectedRoofTopCount: () => undefined,
  dealerSelectedRooftopIds: '',
  setDealerSelectedRooftopIds: () => undefined,
  initialSelectedRooftopIds: [],
  setInitialSelectedRooftopIds: () => undefined,
  roofTopsList: null,
  shouldLoadMoreRooftops: false,
  hasNextRooftopsPage: false,
  fetchNextRooftopsPage: () => undefined,
  selectedTicketsIds: [],
  setSelectedTicketsIds: () => undefined,
  activeTab: ActiveTabTypeE.Tickets,
  setActiveTab: () => undefined,
  isScheduleTicketList: false,
  setIsScheduleTicketList: () => undefined,
  ticketsTotalCount: 0,
};

export const TicketsContext = createContext<TicketsContextT>(contextDefaultValue);

const TicketsProvider: FC<TicketsProviderPropsT> = ({ children }) => {
  /**
   * Hooks
   */
  const [ticketsViewMode, setTicketsViewMode] = useState<TicketsViewModeE>(TicketsViewModeE.List);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [isNewTicketPopupOpened, setNewTicketPopupVisibility] = useState<boolean>(false);
  const [isROListPopupOpened, setROListPopupVisibility] = useState<boolean>(false);
  let [searchParams, setSearchParams] = useSearchParams();
  const [selectedRoofTop, setSelectedRoofTop] = useState<string>('');
  const navigate = useNavigate();
  const [activeScheduleCards, setActiveScheduleCards] = useState<string[]>([]);
  const [activeScheduleFilters, setActiveScheduleFilters] = useState<ScheduleFilterValuesE[]>([]);
  const [defaultRooftopId, setDefaultRooftopId] = useState<string>();
  const [scheduleCategoryId, setScheduleCategoryId] = useState<number>(0);
  const [dealerSelectedRoofTopCount, setDealerSelectedRoofTopCount] = useState(0);
  const [dealerSelectedRooftopIds, setDealerSelectedRooftopIds] = useState<string | null>('');
  const [initialSelectedRooftopIds, setInitialSelectedRooftopIds] = useState<string[]>([]);
  const [selectedTicketsIds, setSelectedTicketsIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTabTypeE>(
    (sessionStorage.getItem('activeTab') as ActiveTabTypeE) || ActiveTabTypeE.Tickets
  );
  const [isScheduleTicketList, setIsScheduleTicketList] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  /** Accumulates multiple updateSearchParamsArray calls in the same tick (e.g. ScheduleCards sets SubCategory + Schedule) */
  const pendingParamsRef = useRef<URLSearchParams | null>(null);

  /**
   * Variables
   */
  const { isMobile } = useResponsive();
  const [isSidebarOpened, setSidebarIsOpen] = useState(true);
  const currentPage = 1;
  const ROOFTOPS_PAGE_SIZE = 25;
  const ticketsPerPage = 25;

  // On Tickets/Insights tab never send schedule category to API so schedule tickets never appear there
  const categoryParamForApi = useMemo(() => {
    const raw = searchParams.get(TicketsRefinementKeysE.Category);
    if (activeTab === ActiveTabTypeE.Schedule) {
      return raw || undefined;
    }
    if (!raw) return undefined;
    const ids = raw
      .split(',')
      .map(c => Number(c.trim()))
      .filter(n => !Number.isNaN(n));
    const hasValidScheduleId = Number.isFinite(scheduleCategoryId) && scheduleCategoryId > 0;
    const filtered = hasValidScheduleId ? ids.filter(id => id !== scheduleCategoryId) : ids;
    return filtered.length > 0 ? filtered.join(',') : undefined;
  }, [searchParams, activeTab, scheduleCategoryId]);

  const fetchTicketsParams = useMemo(() => {
    // Only send roofTopId when user has selected a rooftop in the URL (sidebar).
    // When all dealers are shown (no RoofTopId in URL), do not send any id so the API returns all tickets.
    const roofTopIdForRequest =
      (searchParams.get(TicketsRefinementKeysE.RoofTopId) as string) || undefined;
    return {
      [TicketsRefinementKeysE.RoofTopId]: roofTopIdForRequest,
      [TicketsRefinementKeysE.Status]:
        (searchParams.get(TicketsRefinementKeysE.Status) as StatusFilterValuesE) || undefined,
      [TicketsRefinementKeysE.TicketAge]:
        (searchParams.get(TicketsRefinementKeysE.TicketAge) as AgeFilterValuesE) || undefined,
      [TicketsRefinementKeysE.RONumber]:
        searchParams.get(TicketsRefinementKeysE.RONumber) || undefined,
      [TicketsRefinementKeysE.SearchTerm]:
        searchParams.get(TicketsRefinementKeysE.SearchTerm) || undefined,
      [TicketsRefinementKeysE.AssignedToMe]:
        !!searchParams.get(TicketsRefinementKeysE.AssignedToMe) || undefined,
      [TicketsRefinementKeysE.PastMonth]:
        !!searchParams.get(TicketsRefinementKeysE.PastMonth) || undefined,
      [TicketsRefinementKeysE.Past30Days]:
        !!searchParams.get(TicketsRefinementKeysE.Past30Days) || undefined,
      [TicketsRefinementKeysE.IsArchived]:
        !!searchParams.get(TicketsRefinementKeysE.IsArchived) || false,
      [TicketsRefinementKeysE.Parameter]:
        (searchParams.get(TicketsRefinementKeysE.Parameter) as ParameterSortingValuesE) ||
        undefined,
      [TicketsRefinementKeysE.Order]:
        (searchParams.get(TicketsRefinementKeysE.Order) as SortingOrderE) || undefined,
      [TicketsRefinementKeysE.CurrentPage]:
        (searchParams.get(TicketsRefinementKeysE.CurrentPage) as string) || currentPage.toString(),
      [TicketsRefinementKeysE.CountPerPage]:
        (searchParams.get(TicketsRefinementKeysE.CountPerPage) as string) ||
        ticketsPerPage.toString(),
      [TicketsRefinementKeysE.Schedule]:
        activeTab === ActiveTabTypeE.Schedule
          ? (searchParams.get(TicketsRefinementKeysE.Schedule) as ScheduleFilterValuesE) ||
            undefined
          : undefined,
      [TicketsRefinementKeysE.Category]: categoryParamForApi,
      [TicketsRefinementKeysE.SubCategory]:
        activeTab === ActiveTabTypeE.Schedule
          ? searchParams.get(TicketsRefinementKeysE.SubCategory) || undefined
          : undefined,
      [TicketsRefinementKeysE.RequestSubCategoryIds]:
        activeTab === ActiveTabTypeE.Schedule
          ? searchParams.get(TicketsRefinementKeysE.RequestSubCategoryIds) || undefined
          : undefined,
      [TicketsRefinementKeysE.Assignee]:
        searchParams.get(TicketsRefinementKeysE.Assignee) || undefined,
    };
  }, [searchParams, activeTab, categoryParamForApi, currentPage, ticketsPerPage]);
  const getSearchParam = useCallback(
    (key: TicketsRefinementKeysE) => searchParams.get(key),
    [searchParams]
  );
  const fetchCountByRooftopsParams = useMemo(
    () => ({
      paging: {
        currentPage: currentPage,
        countPerPage: ROOFTOPS_PAGE_SIZE,
      },
      roofTopIds: dealerSelectedRooftopIds,
    }),
    [currentPage, ROOFTOPS_PAGE_SIZE, dealerSelectedRooftopIds]
  );
  /**
   * Api hooks
   */
  const { userProfile } = useUserProfile();
  const ticketsResult = useFetchTickets(fetchTicketsParams);
  const ticketsCountByRoofTopResult = useFetchTicketsCountByRoofTop(fetchCountByRooftopsParams);
  const categoriesResult = useFetchTicketRequestCategories(
    true,
    selectedRoofTop ?? defaultRooftopId
  );
  /**
   * Variables
   */
  const fetchTicketsErrorMessage = useMemo(
    () => getErrorMessage(ticketsResult.error),
    [ticketsResult.error]
  );
  const fetchTicketsValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(ticketsResult.error),
    [ticketsResult.error]
  );

  /**
   * Handlers
   */
  const getAllSearchParam = useCallback(
    (key: TicketsRefinementKeysE) => searchParams.getAll(key).map(Number),
    [searchParams]
  );

  const updateSearchParam = useCallback(
    (key: TicketsRefinementKeysE, value: string) => {
      searchParams.set(key, value);
      setSearchParams(searchParams);
      if (isMobile) {
        setSidebarIsOpen(false);
      }
    },
    [isMobile, searchParams, setSearchParams, setSidebarIsOpen]
  );
  const clearSearchParam = useCallback(
    (keys: TicketsRefinementKeysE | TicketsRefinementKeysE[]) => {
      if (Array.isArray(keys)) {
        keys.forEach(key => {
          searchParams.delete(key);
        });
      }

      if (typeof keys === 'string') {
        searchParams.delete(keys);
      }

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const updateSearchParamsArray = useCallback(
    (
      key: TicketsRefinementKeysE,
      valuesArray: (number | string | ScheduleFilterValuesE)[] | null
    ) => {
      if (!pendingParamsRef.current) {
        pendingParamsRef.current = new URLSearchParams(window.location.search);
      }
      const params = pendingParamsRef.current;
      if (valuesArray != null && valuesArray.length > 0) {
        params.set(key, valuesArray.join(','));
      } else {
        params.delete(key);
      }
      const queryString =
        window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      if (isMobile) {
        setSidebarIsOpen(false);
      }
      setPendingNavigation(queryString);
    },
    [isMobile, setSidebarIsOpen]
  );

  /**
   * Effects
   */
  // Handle pending navigation updates to avoid calling navigate() during render
  useEffect(() => {
    if (pendingNavigation) {
      navigate(pendingNavigation, { replace: true });
      setPendingNavigation(null);
      pendingParamsRef.current = null;
    }
  }, [pendingNavigation, navigate]);

  useEffect(() => {
    setSelectedRoofTop(getSearchParam(TicketsRefinementKeysE.RoofTopId) ?? '');
  }, [getSearchParam]);

  useEffect(() => {
    if (ticketsCountByRoofTopResult.data && ticketsCountByRoofTopResult.data.pages.length > 0) {
      const countByRoofTops = ticketsCountByRoofTopResult.data.pages.flatMap(
        (page: any) => page?.countByRoofTop || []
      );

      if (countByRoofTops.length > 0) {
        setDefaultRooftopId(countByRoofTops[0]?.roofTopId);
      } else {
        setDefaultRooftopId(undefined);
      }
    }
  }, [ticketsCountByRoofTopResult.data]);

  useEffect(() => {
    if (defaultRooftopId) {
      categoriesResult.refetch().then(() => {
        const scheduleCategory = categoriesResult?.data?.filter(
          category => category.categoryName.toLowerCase() === 'schedule'
        );
        const scheduleCategoryIdArr = scheduleCategory?.map(category => category.id.toString());
        const parsedScheduleCategoryId = Number(scheduleCategoryIdArr?.[0]);
        if (Number.isFinite(parsedScheduleCategoryId) && parsedScheduleCategoryId > 0) {
          setScheduleCategoryId(parsedScheduleCategoryId);
        }
      });
    }
    // eslint-disable-next-line
  }, [defaultRooftopId, searchParams]);

  // When on Tickets or Insights tab, remove schedule category from URL so params stay in sync
  useEffect(() => {
    if (activeTab === ActiveTabTypeE.Schedule) return;
    const hasValidScheduleId = Number.isFinite(scheduleCategoryId) && scheduleCategoryId > 0;
    if (!hasValidScheduleId) return;
    const categoryParam = searchParams.get(TicketsRefinementKeysE.Category);
    if (!categoryParam) return;
    const ids = categoryParam
      .split(',')
      .map(c => Number(c.trim()))
      .filter(n => !Number.isNaN(n));
    if (!ids.includes(scheduleCategoryId)) return;
    const filtered = ids.filter(id => id !== scheduleCategoryId);
    const next = new URLSearchParams(searchParams);
    if (filtered.length > 0) {
      next.set(TicketsRefinementKeysE.Category, filtered.join(','));
    } else {
      next.delete(TicketsRefinementKeysE.Category);
    }
    setSearchParams(next, { replace: true });
  }, [activeTab, scheduleCategoryId, searchParams, setSearchParams]);

  // When on Tickets or Insights tab, remove schedule-only params
  useEffect(() => {
    if (activeTab === ActiveTabTypeE.Schedule) return;
    const scheduleOnlyKeys = [
      TicketsRefinementKeysE.Schedule,
      TicketsRefinementKeysE.SubCategory,
      TicketsRefinementKeysE.RequestSubCategoryIds,
      TicketsRefinementKeysE.DMSScheduleAccounts,
    ];
    const hasAny = scheduleOnlyKeys.some(key => searchParams.has(key));
    if (!hasAny) return;
    setActiveScheduleCards([]);
    setActiveScheduleFilters([]);
    const next = new URLSearchParams(searchParams);
    scheduleOnlyKeys.forEach(key => next.delete(key));
    setSearchParams(next, { replace: true });
  }, [activeTab, searchParams, setSearchParams, setActiveScheduleCards, setActiveScheduleFilters]);

  const combinedTicketsCountByRoofTop = useMemo(() => {
    if (ticketsCountByRoofTopResult.data?.pages) {
      return ticketsCountByRoofTopResult.data.pages.reduce<TicketCountByRoofTopI[]>((acc, page) => {
        if (
          page?.countByRoofTop &&
          Array.isArray(page.countByRoofTop) &&
          page.countByRoofTop.length > 0
        ) {
          return acc.concat(page.countByRoofTop);
        }
        return acc;
      }, []);
    }
    return [];
  }, [ticketsCountByRoofTopResult.data]);

  const ticketsTotalCountForTab = useMemo(() => {
    const selectedRoofTopId = searchParams.get(TicketsRefinementKeysE.RoofTopId);
    if (selectedRoofTopId) {
      const rooftop = combinedTicketsCountByRoofTop.find(r => r.roofTopId === selectedRoofTopId);
      return rooftop?.total ?? 0;
    }
    const firstPage = ticketsCountByRoofTopResult.data?.pages?.[0];
    return (firstPage as { total?: number } | undefined)?.total ?? 0;
  }, [searchParams, combinedTicketsCountByRoofTop, ticketsCountByRoofTopResult.data?.pages]);

  return (
    <TicketsContext.Provider
      value={{
        ticketsPages: ticketsResult.data?.pages ?? null,
        ticketsCount: ticketsResult.data?.pages[0].count || 0,
        isTicketsFetchingError: ticketsResult.isError,
        fetchTicketsErrorMessage,
        fetchTicketsValidationErrorMessage,
        mappedRooftopsList: combinedTicketsCountByRoofTop,
        isLoadingTickets: ticketsResult.isLoading || ticketsResult.isRefetching,
        isSuccessfullyFetchedTickets: ticketsResult.isSuccess,
        ticketsCountByRoofTop: ticketsCountByRoofTopResult.data?.pages[0],
        isTicketsFetchingCountByRoofTopError: ticketsCountByRoofTopResult.isError,
        isLoadingTicketsCountByRoofTop:
          ticketsCountByRoofTopResult.isLoading || ticketsCountByRoofTopResult.isFetching,
        setSidebarIsOpen,
        isSidebarOpened: isSidebarOpened,
        setTicketsViewMode,
        ticketsViewMode,
        setTicketId,
        selectedTicketId: ticketId,
        isNewTicketPopupOpened,
        setNewTicketPopupVisibility,
        isROListPopupOpened,
        setROListPopupVisibility,
        getSearchParam,
        updateSearchParam,
        getAllSearchParam,
        updateSearchParamsArray,
        clearSearchParam,
        isFetchingNextTicketsPage: ticketsResult.isFetchingNextPage,
        isFetchingTickets: ticketsResult.isFetching,
        fetchNextTicketsPage: ticketsResult.fetchNextPage,
        hasNextTicketsPage: ticketsResult.hasNextPage,
        fetchTicketsParams,
        activeScheduleCards,
        setActiveScheduleCards,
        activeScheduleFilters,
        setActiveScheduleFilters,
        selectedRoofTop,
        scheduleCategoryId,
        setScheduleCategoryId,
        dealerSelectedRoofTopCount,
        setDealerSelectedRoofTopCount,
        setDealerSelectedRooftopIds,
        dealerSelectedRooftopIds,
        initialSelectedRooftopIds,
        setInitialSelectedRooftopIds,
        roofTopsList: userProfile?.mappedRoofTops || null,
        hasNextRooftopsPage: Boolean(ticketsCountByRoofTopResult.hasNextPage),
        fetchNextRooftopsPage: ticketsCountByRoofTopResult.fetchNextPage,
        shouldLoadMoreRooftops: Boolean(
          ticketsCountByRoofTopResult.isRefetching &&
            ticketsCountByRoofTopResult.isFetchingNextPage &&
            ticketsCountByRoofTopResult.hasNextPage
        ),
        selectedTicketsIds,
        setSelectedTicketsIds,
        activeTab,
        setActiveTab,
        isScheduleTicketList,
        setIsScheduleTicketList,
        ticketsTotalCount: ticketsTotalCountForTab,
      }}
    >
      {children}
    </TicketsContext.Provider>
  );
};

const useTickets = () => useContext(TicketsContext);

export { TicketsProvider, useTickets };
